// Customer Reports — energy production over time.
//   • dropdown filter: Today / Yesterday / This month / Last month / Custom
//   • KPI cards: total energy, peak power, exported, earnings
//   • SVG line chart of solar power
//   • Daily summary

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Divider,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import ElectricMeterOutlinedIcon from "@mui/icons-material/ElectricMeterOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as XLSX from "xlsx";
import { fetchMyTelemetry, fetchMyHistory } from "../features/telemetry/telemetrySlice";
import { useAnimatedNumber } from "../features/telemetry/AnimatedNumber";

const RUPEE_PER_KWH = 3.5;
const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });
const numFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

const RANGE_OPTIONS = [
  { id: "today",      label: "Today" },
  { id: "yesterday",  label: "Yesterday" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "custom",     label: "Custom…" },
];

const num = (v) => {
  if (v == null || v === "") return null;
  const n = typeof v === "string" ? parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : null;
};

// Compute the [fromMs, toMs] window for a given range option.
function rangeBounds(rangeId, customFrom, customTo) {
  const now = new Date();
  if (rangeId === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return { from: start, to: Date.now() };
  }
  if (rangeId === "yesterday") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() - 1;
    return { from: start, to: end };
  }
  if (rangeId === "this_month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return { from: start, to: Date.now() };
  }
  if (rangeId === "last_month") {
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
    const end = new Date(now.getFullYear(), now.getMonth(), 1).getTime() - 1;
    return { from: start, to: end };
  }
  if (rangeId === "custom") {
    const from = customFrom ? new Date(customFrom + "T00:00:00").getTime() : null;
    const to = customTo ? new Date(customTo + "T23:59:59").getTime() : null;
    return { from, to };
  }
  return { from: null, to: null };
}

function rangeLabel(rangeId, customFrom, customTo) {
  if (rangeId === "custom") {
    if (customFrom && customTo) return `${customFrom} → ${customTo}`;
    return "Custom";
  }
  return RANGE_OPTIONS.find((r) => r.id === rangeId)?.label || "—";
}

const fmtTime = (d, spanMs) => {
  if (spanMs < 48 * 3600 * 1000) {
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  }
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
};

// ── KPI card ─────────────────────────────────────────────────────────
const Kpi = ({ icon, label, value, unit, rupees, accent, delay = "" }) => {
  const animValue = useAnimatedNumber(Number(value) || 0);
  const animInr = useAnimatedNumber(Number(rupees) || 0);
  return (
    <Box
      className={`fade-up${delay ? ` fade-up-delay-${delay}` : ""}`}
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1.25, sm: 2 },
        borderRadius: { xs: 2, sm: 3 },
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "0 1px 2px rgba(13,46,117,0.04), 0 6px 18px rgba(13,46,117,0.05)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          right: -28, top: -28,
          width: 86, height: 86, borderRadius: "50%",
          background: `radial-gradient(circle, ${accent}26 0%, ${accent}00 70%)`,
          pointerEvents: "none",
        }}
      />
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} spacing={{ xs: 0.5, sm: 1.25 }}>
        <Box
          sx={{
            width: { xs: 28, sm: 36 }, height: { xs: 28, sm: 36 },
            borderRadius: 1.5,
            background: accent,
            color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            "& svg": { fontSize: { xs: 16, sm: 20 } },
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary", fontWeight: 600,
              textTransform: "uppercase", letterSpacing: 0.5,
              fontSize: { xs: "0.5625rem", sm: "0.6875rem" },
              lineHeight: 1.2, display: "block",
            }}
          >
            {label}
          </Typography>
          <Stack direction="row" alignItems="baseline" spacing={0.5}>
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.25rem" },
                fontWeight: 800, fontFamily: "monospace",
                letterSpacing: -0.4, lineHeight: 1.1,
              }}
            >
              {numFmt.format(animValue.toFixed(animValue >= 100 ? 0 : 2))}
            </Typography>
            {unit && (
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, fontSize: { xs: "0.625rem", sm: "0.75rem" } }}>
                {unit}
              </Typography>
            )}
          </Stack>
          {rupees != null && (
            <Typography
              sx={{
                fontFamily: "monospace",
                fontWeight: 700,
                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                color: "text.primary",
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              ₹ {inrFmt.format(animInr)}
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
};

// ── SVG line chart ───────────────────────────────────────────────────
const Chart = ({ history, spanMs, height = 200 }) => {
  const target = 120;
  const samples = useMemo(() => {
    if (!history.length) return [];
    if (history.length <= target) return history;
    const step = Math.ceil(history.length / target);
    return history.filter((_, i) => i % step === 0);
  }, [history]);

  if (!samples.length) {
    return (
      <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
        <Typography variant="body2">No data in this range yet.</Typography>
      </Box>
    );
  }

  const data = samples.map((row) => ({
    t: new Date(row.receivedAt).getTime(),
    p: Math.max(0, num(row.d18) ?? 0),
  }));
  const minT = data[0].t;
  const maxT = data[data.length - 1].t;
  const maxP = Math.max(1, ...data.map((d) => d.p));

  const W = 1000;
  const H = height;
  const padL = 40, padR = 12, padT = 16, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const xOf = (t) => padL + ((t - minT) / Math.max(1, maxT - minT)) * innerW;
  const yOf = (p) => padT + innerH - (p / maxP) * innerH;

  const linePath = data
    .map((d, i) => `${i === 0 ? "M" : "L"} ${xOf(d.t).toFixed(1)} ${yOf(d.p).toFixed(1)}`)
    .join(" ");
  const areaPath =
    `M ${xOf(data[0].t).toFixed(1)} ${(padT + innerH).toFixed(1)} ` +
    data.map((d) => `L ${xOf(d.t).toFixed(1)} ${yOf(d.p).toFixed(1)}`).join(" ") +
    ` L ${xOf(data[data.length - 1].t).toFixed(1)} ${(padT + innerH).toFixed(1)} Z`;

  const gridY = [0, 0.25, 0.5, 0.75, 1].map((f) => ({
    y: padT + innerH - innerH * f,
    label: (maxP * f).toFixed(maxP < 10 ? 1 : 0),
  }));
  const xLabels = [
    { t: minT, x: padL },
    { t: minT + (maxT - minT) / 2, x: padL + innerW / 2 },
    { t: maxT, x: padL + innerW },
  ];

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={height} preserveAspectRatio="none">
        <defs>
          <linearGradient id="chartFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#22c55e" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridY.map((g, i) => (
          <g key={i}>
            <line x1={padL} y1={g.y} x2={padL + innerW} y2={g.y} stroke="#e3e8ef" strokeWidth="1" vectorEffect="non-scaling-stroke" />
            <text x={padL - 6} y={g.y + 4} textAnchor="end" fontFamily="Inter, sans-serif" fontSize="11" fill="#5a6573">{g.label}</text>
          </g>
        ))}
        <path d={areaPath} fill="url(#chartFill)" />
        <path d={linePath} fill="none" stroke="#22c55e" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {xLabels.map((l, i) => (
          <text
            key={i}
            x={l.x}
            y={H - 8}
            textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"}
            fontFamily="Inter, sans-serif" fontSize="11" fill="#5a6573"
          >
            {fmtTime(new Date(l.t), spanMs)}
          </text>
        ))}
      </svg>
    </Box>
  );
};

// ── Daily summary ───────────────────────────────────────────────────
const DailySummary = ({ history }) => {
  const days = useMemo(() => {
    if (history.length === 0) return [];
    const byDay = new Map();
    history.forEach((row) => {
      const t = new Date(row.receivedAt).getTime();
      const key = new Date(t).toISOString().slice(0, 10);
      const p = Math.max(0, num(row.d18) ?? 0);
      if (!byDay.has(key)) byDay.set(key, { dateKey: key, count: 0, sumP: 0, maxP: 0 });
      const d = byDay.get(key);
      d.count += 1;
      d.sumP += p;
      if (p > d.maxP) d.maxP = p;
    });
    return Array.from(byDay.values())
      .map((d) => ({
        ...d,
        avgP: d.sumP / d.count,
        energyKwh: (d.sumP / d.count) * (d.count / 60),
      }))
      .sort((a, b) => b.dateKey.localeCompare(a.dateKey))
      .slice(0, 14);
  }, [history]);

  if (days.length === 0) return null;
  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", display: { xs: "none", sm: "flex" } }}
      >
        <Typography variant="caption" sx={{ flex: 1, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.6875rem" }}>Day</Typography>
        <Typography variant="caption" sx={{ width: 80, textAlign: "right", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.6875rem" }}>Peak kW</Typography>
        <Typography variant="caption" sx={{ width: 96, textAlign: "right", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.6875rem" }}>Energy kWh</Typography>
        <Typography variant="caption" sx={{ width: 88, textAlign: "right", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, color: "text.secondary", fontSize: "0.6875rem" }}>Earnings</Typography>
      </Stack>
      {days.map((d) => (
        <Box
          key={d.dateKey}
          sx={{ px: 1.5, py: 1, borderBottom: "1px solid", borderColor: "divider", "&:last-child": { borderBottom: 0 } }}
        >
          <Stack direction="row" alignItems="center" sx={{ display: { xs: "none", sm: "flex" } }}>
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{d.dateKey}</Typography>
            <Typography variant="body2" sx={{ width: 80, textAlign: "right", fontFamily: "monospace" }}>{d.maxP.toFixed(2)}</Typography>
            <Typography variant="body2" sx={{ width: 96, textAlign: "right", fontFamily: "monospace" }}>{d.energyKwh.toFixed(2)}</Typography>
            <Typography variant="body2" sx={{ width: 88, textAlign: "right", fontFamily: "monospace", color: "primary.main", fontWeight: 600 }}>
              ₹ {inrFmt.format(d.energyKwh * RUPEE_PER_KWH)}
            </Typography>
          </Stack>
          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={0.25}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{d.dateKey}</Typography>
              <Typography variant="body2" sx={{ fontFamily: "monospace", color: "primary.main", fontWeight: 700 }}>
                ₹ {inrFmt.format(d.energyKwh * RUPEE_PER_KWH)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Peak <Box component="span" sx={{ color: "text.primary", fontFamily: "monospace", fontWeight: 600 }}>{d.maxP.toFixed(2)} kW</Box>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Energy <Box component="span" sx={{ color: "text.primary", fontFamily: "monospace", fontWeight: 600 }}>{d.energyKwh.toFixed(2)} kWh</Box>
              </Typography>
            </Stack>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

// ── Custom range dialog ──────────────────────────────────────────────
function CustomRangeDialog({ open, from, to, onClose, onApply }) {
  const [f, setF] = useState(from || "");
  const [t, setT] = useState(to || "");
  useEffect(() => { setF(from || ""); setT(to || ""); }, [from, to, open]);

  const submit = () => {
    if (!f || !t) return;
    if (new Date(f) > new Date(t)) return;
    onApply(f, t);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>Custom range</DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2}>
          <TextField
            label="From"
            type="date"
            value={f}
            onChange={(e) => setF(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: t || undefined }}
          />
          <TextField
            label="To"
            type="date"
            value={t}
            onChange={(e) => setT(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: f || undefined }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!f || !t}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────
export default function ReportPage() {
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { items, history, historyLoading } = useSelector((s) => s.telemetry);
  const [rangeId, setRangeId] = useState("today");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [customOpen, setCustomOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // null | "pdf" | "xlsx"
  const reportRef = useRef(null);

  useEffect(() => {
    if (items.length === 0) dispatch(fetchMyTelemetry());
  }, [dispatch, items.length]);

  // Window bounds for the active range
  const bounds = useMemo(
    () => rangeBounds(rangeId, customFrom, customTo),
    [rangeId, customFrom, customTo]
  );

  const imei = items[0]?.device?.imei;
  useEffect(() => {
    if (!imei) return;
    if (!bounds.from || !bounds.to) return; // wait until custom is filled in
    const minutes = Math.max(60, Math.ceil((bounds.to - bounds.from) / 60000) + 60);
    dispatch(fetchMyHistory({
      imei,
      sinceMs: bounds.from,
      toMs: bounds.to,
      limit: Math.min(20000, minutes),
    }));
  }, [dispatch, imei, bounds.from, bounds.to]);

  // Range select handler
  const onRangeChange = (e) => {
    const v = e.target.value;
    setRangeId(v);
    if (v === "custom") setCustomOpen(true);
  };

  const stats = useMemo(() => {
    if (history.length === 0) {
      return {
        energyKwh: 0, peakKw: 0, avgKw: 0,
        exportKwh: 0, importKwh: 0,
        earnings: 0, exportEarnings: 0, importCost: 0,
      };
    }
    let peakKw = 0, sumP = 0, count = 0;
    history.forEach((row) => {
      const p = Math.max(0, num(row.d18) ?? 0);
      if (p > peakKw) peakKw = p;
      sumP += p; count += 1;
    });
    const avgKw = count ? sumP / count : 0;
    const minT = new Date(history[0].receivedAt).getTime();
    const maxT = new Date(history[history.length - 1].receivedAt).getTime();
    const spanHrs = Math.max(0.01, (maxT - minT) / 3600000);

    // Prefer cumulative deltas when the cumulative field is present —
    // they're firmware-accurate. Fall back to time-integrated avg power
    // if the cumulative value isn't available.
    const delta = (key) => {
      const first = num(history[0][key]);
      const last  = num(history[history.length - 1][key]);
      if (first == null || last == null) return null;
      return Math.max(0, last - first);
    };
    const energyKwh = delta("d17") ?? avgKw * spanHrs; // solar.totalEnergy_kWh
    const exportKwh = delta("d15") ?? 0;               // meter.export_kWh_total
    const importKwh = delta("d14") ?? 0;               // meter.import_kWh_total

    return {
      energyKwh, peakKw, avgKw,
      exportKwh, importKwh,
      earnings:       energyKwh * RUPEE_PER_KWH,
      exportEarnings: exportKwh * RUPEE_PER_KWH,
      importCost:     importKwh * RUPEE_PER_KWH,
    };
  }, [history]);

  const spanMs = (bounds.to || 0) - (bounds.from || 0);

  // Compute daily summary once so it can feed both UI and Excel export.
  const daysSummary = useMemo(() => {
    if (history.length === 0) return [];
    const byDay = new Map();
    history.forEach((row) => {
      const t = new Date(row.receivedAt).getTime();
      const key = new Date(t).toISOString().slice(0, 10);
      const p = Math.max(0, num(row.d18) ?? 0);
      if (!byDay.has(key)) byDay.set(key, { dateKey: key, count: 0, sumP: 0, maxP: 0 });
      const d = byDay.get(key);
      d.count += 1;
      d.sumP += p;
      if (p > d.maxP) d.maxP = p;
    });
    return Array.from(byDay.values())
      .map((d) => ({
        ...d,
        avgP: d.sumP / d.count,
        energyKwh: (d.sumP / d.count) * (d.count / 60),
      }))
      .sort((a, b) => a.dateKey.localeCompare(b.dateKey));
  }, [history]);

  const exportSlug = useMemo(() => {
    const safeRange = rangeLabel(rangeId, customFrom, customTo)
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .toLowerCase();
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    return `surya-solar-report_${safeRange}_${stamp}`;
  }, [rangeId, customFrom, customTo]);

  // ── PDF export ──────────────────────────────────────────────────────
  const handleDownloadPdf = async () => {
    if (!reportRef.current) return;
    setExporting("pdf");
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });
      const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const imgW = pageW - margin * 2;
      const imgH = (canvas.height * imgW) / canvas.width;

      // Header line on the PDF itself
      pdf.setFontSize(11);
      pdf.setTextColor(13, 46, 117);
      pdf.text("Surya Solars — Energy Report", margin, margin + 4);
      pdf.setFontSize(9);
      pdf.setTextColor(90, 101, 115);
      pdf.text(
        `${user?.company?.name || "Plant"}  ·  ${rangeLabel(rangeId, customFrom, customTo)}  ·  generated ${new Date().toLocaleString()}`,
        margin,
        margin + 9
      );

      let y = margin + 13;
      // If the image is taller than one page, paginate
      let remaining = imgH;
      let sourceY = 0;
      const usableH = pageH - y - margin;
      while (remaining > 0) {
        const sliceH = Math.min(remaining, usableH);
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = (sliceH / imgH) * canvas.height;
        const ctx = sliceCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0, sourceY, canvas.width, sliceCanvas.height,
          0, 0, canvas.width, sliceCanvas.height
        );
        pdf.addImage(sliceCanvas.toDataURL("image/png"), "PNG", margin, y, imgW, sliceH);
        remaining -= sliceH;
        sourceY += sliceCanvas.height;
        if (remaining > 0) {
          pdf.addPage();
          y = margin;
        }
      }
      pdf.save(`${exportSlug}.pdf`);
      toast.success("PDF downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export PDF");
    } finally {
      setExporting(null);
    }
  };

  // ── Excel export ───────────────────────────────────────────────────
  const handleDownloadExcel = () => {
    setExporting("xlsx");
    try {
      const wb = XLSX.utils.book_new();

      // 1. Summary
      const summary = [
        ["Surya Solars — Energy Report"],
        [],
        ["Plant",     user?.company?.name || "—"],
        ["Customer",  user?.name || "—"],
        ["IMEI",      items[0]?.device?.imei || "—"],
        ["Range",     rangeLabel(rangeId, customFrom, customTo)],
        ["From",      bounds.from ? new Date(bounds.from).toLocaleString() : "—"],
        ["To",        bounds.to ? new Date(bounds.to).toLocaleString() : "—"],
        ["Generated", new Date().toLocaleString()],
        [],
        ["Metric",            "Value",                                    "Unit"],
        ["Energy Generated",  Number(stats.energyKwh.toFixed(2)),         "kWh"],
        ["Solar Earnings",    Number(stats.earnings.toFixed(2)),          "INR"],
        ["Peak Power",        Number(stats.peakKw.toFixed(2)),            "kW"],
        ["EB Export",         Number(stats.exportKwh.toFixed(2)),         "kWh"],
        ["EB Export Value",   Number(stats.exportEarnings.toFixed(2)),    "INR"],
        ["EB Import",         Number(stats.importKwh.toFixed(2)),         "kWh"],
        ["EB Import Cost",    Number(stats.importCost.toFixed(2)),        "INR"],
        ["Rate",              RUPEE_PER_KWH,                              "INR/kWh"],
      ];
      const wsSummary = XLSX.utils.aoa_to_sheet(summary);
      wsSummary["!cols"] = [{ wch: 22 }, { wch: 28 }, { wch: 10 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      // 2. Daily
      const dailyHeader = ["Day", "Peak kW", "Avg kW", "Energy kWh", "Earnings INR"];
      const dailyRows = daysSummary.map((d) => [
        d.dateKey,
        Number(d.maxP.toFixed(2)),
        Number(d.avgP.toFixed(2)),
        Number(d.energyKwh.toFixed(2)),
        Number((d.energyKwh * RUPEE_PER_KWH).toFixed(2)),
      ]);
      const wsDaily = XLSX.utils.aoa_to_sheet([dailyHeader, ...dailyRows]);
      wsDaily["!cols"] = [{ wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 14 }];
      XLSX.utils.book_append_sheet(wb, wsDaily, "Daily");

      // 3. Raw history
      const rawHeader = [
        "Timestamp", "Solar kW (d18)", "EB Grid kW (d13)",
        "Today kWh (d16)", "Total kWh (d17)",
        "Import kWh (d14)", "Export kWh (d15)",
        "Grid V (d20)", "Online (isonline)",
      ];
      const rawRows = history.map((h) => [
        new Date(h.receivedAt).toISOString(),
        num(h.d18), num(h.d13), num(h.d16), num(h.d17),
        num(h.d14), num(h.d15), num(h.d20),
        h.isonline === 1 ? 1 : 0,
      ]);
      const wsRaw = XLSX.utils.aoa_to_sheet([rawHeader, ...rawRows]);
      wsRaw["!cols"] = [
        { wch: 22 }, { wch: 14 }, { wch: 16 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
        { wch: 10 }, { wch: 10 },
      ];
      XLSX.utils.book_append_sheet(wb, wsRaw, "Raw");

      XLSX.writeFile(wb, `${exportSlug}.xlsx`);
      toast.success("Excel downloaded");
    } catch (err) {
      console.error(err);
      toast.error("Failed to export Excel");
    } finally {
      setExporting(null);
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 1, md: 3 },
        py: { xs: 1.5, md: 3 },
        maxWidth: 1100,
        mx: "auto",
        width: "100%",
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h2" sx={{ fontSize: "1.25rem", fontWeight: 600 }}>
            Reports
          </Typography>
          {historyLoading && <CircularProgress size={16} />}
        </Stack>

        {/* Export buttons — icon-only on phones */}
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Download PDF">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting === "pdf" ? <CircularProgress size={14} /> : <PictureAsPdfOutlinedIcon />}
                onClick={handleDownloadPdf}
                disabled={!!exporting || historyLoading || history.length === 0}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  borderColor: "#bb0000",
                  color: "#bb0000",
                  "&:hover": { borderColor: "#a30000", backgroundColor: "rgba(187,0,0,0.04)" },
                }}
              >
                PDF
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download Excel">
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={exporting === "xlsx" ? <CircularProgress size={14} /> : <TableChartOutlinedIcon />}
                onClick={handleDownloadExcel}
                disabled={!!exporting || historyLoading || history.length === 0}
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  borderColor: "#0e7e3e",
                  color: "#0e7e3e",
                  "&:hover": { borderColor: "#0a6231", backgroundColor: "rgba(14,126,62,0.04)" },
                }}
              >
                Excel
              </Button>
            </span>
          </Tooltip>

          {/* Phone: compact icon buttons */}
          <Tooltip title="Download PDF">
            <span>
              <IconButton
                size="small"
                onClick={handleDownloadPdf}
                disabled={!!exporting || historyLoading || history.length === 0}
                sx={{ display: { xs: "inline-flex", sm: "none" }, color: "#bb0000" }}
              >
                {exporting === "pdf" ? <CircularProgress size={16} /> : <PictureAsPdfOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download Excel">
            <span>
              <IconButton
                size="small"
                onClick={handleDownloadExcel}
                disabled={!!exporting || historyLoading || history.length === 0}
                sx={{ display: { xs: "inline-flex", sm: "none" }, color: "#0e7e3e" }}
              >
                {exporting === "xlsx" ? <CircularProgress size={16} /> : <TableChartOutlinedIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* The PDF screenshot wraps everything below */}
      <Box ref={reportRef}>

      {/* Dropdown filter */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "stretch", sm: "center" }}
        mb={2}
      >
        <TextField
          select
          size="small"
          value={rangeId}
          onChange={onRangeChange}
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EventOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
        >
          {RANGE_OPTIONS.map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Chip
          label={rangeLabel(rangeId, customFrom, customTo)}
          variant="outlined"
          onClick={rangeId === "custom" ? () => setCustomOpen(true) : undefined}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {/* KPI cards — 2×2 on phones, 4-up on sm+. Green = generation,
          red = grid, matching the dashboard's colour grammar. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
          gap: { xs: 1, sm: 1.5 },
          mb: 2,
        }}
      >
        <Kpi
          icon={<SolarPowerOutlinedIcon />}
          label="Energy Generated"
          value={stats.energyKwh}
          unit="kWh"
          rupees={stats.earnings}
          accent="#22c55e"
          delay="1"
        />
        <Kpi
          icon={<SolarPowerOutlinedIcon />}
          label="Peak Power"
          value={stats.peakKw}
          unit="kW"
          accent="#16a34a"
          delay="2"
        />
        <Kpi
          icon={<ElectricMeterOutlinedIcon />}
          label="EB Export"
          value={stats.exportKwh}
          unit="kWh"
          rupees={stats.exportEarnings}
          accent="#dc2626"
          delay="3"
        />
        <Kpi
          icon={<BoltOutlinedIcon />}
          label="EB Import"
          value={stats.importKwh}
          unit="kWh"
          rupees={stats.importCost}
          accent="#dc2626"
          delay="4"
        />
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Box>
            <Typography variant="h5">Solar Production</Typography>
            <Typography variant="caption" color="text.secondary">
              kW over time · {rangeLabel(rangeId, customFrom, customTo)}
            </Typography>
          </Box>
          {history.length > 0 && (
            <Chip size="small" label={`${history.length} samples`} variant="outlined" sx={{ fontFamily: "monospace" }} />
          )}
        </Stack>
        <Divider sx={{ mb: 1 }} />
        {historyLoading && history.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={22} /></Box>
        ) : (
          <Chart history={history} spanMs={spanMs} height={220} />
        )}
      </Paper>

      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Stack sx={{ px: 1.5, py: 1.25 }}>
          <Typography variant="h5">Daily summary</Typography>
          <Typography variant="caption" color="text.secondary">
            Earnings @ ₹{RUPEE_PER_KWH}/kWh
          </Typography>
        </Stack>
        <Divider />
        <DailySummary history={history} />
        {history.length === 0 && !historyLoading && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">No data for this range.</Typography>
          </Box>
        )}
      </Paper>

      </Box>
      {/* /reportRef */}

      <CustomRangeDialog
        open={customOpen}
        from={customFrom}
        to={customTo}
        onClose={() => setCustomOpen(false)}
        onApply={(f, t) => {
          setCustomFrom(f);
          setCustomTo(t);
          setCustomOpen(false);
        }}
      />
    </Box>
  );
}
